
import React from 'react';
import { useTranslation } from 'react-i18next';

interface HeroProps {
  onExplore: () => void;
  onLife?: () => void;
}

const Hero: React.FC<HeroProps> = ({ onExplore, onLife }) => {
  const { t } = useTranslation();

  return (
    <>
      <section className="relative h-[70vh] min-h-[500px] sm:h-[700px] md:h-[750px] w-full flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="/images/hero-diverse-team-meeting.webp"
            alt={t('hero.imageAlt')}
            className="w-full h-full object-cover object-center"
            style={{ objectPosition: 'center 30%' }}
            loading="eager"
            fetchPriority="high"
          />
          {/* 明亮的渐变遮罩 - 左侧深一点保证文字可读，右侧透亮 */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/75 via-blue-900/50 to-transparent" />
          {/* 添加光晕装饰 */}
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-blue-400/15 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full pt-16 sm:pt-20 animate-fade-in">
          <div className="max-w-2xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-blue-600/90 border border-blue-400/50 rounded-full px-3 sm:px-4 py-1.5 text-white text-xs sm:text-sm font-medium mb-5 sm:mb-7 backdrop-blur-md shadow-lg">
              <span className="flex h-2 w-2 rounded-full bg-green-400 animate-pulse"></span>
              {t('hero.badge')}
            </div>

            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white tracking-tight leading-[1.1] mb-5 sm:mb-7">
              {t('hero.title')}
              <br className="mb-2" />
              <span className="text-blue-400 block mt-2">{t('hero.titleHighlight')}</span>
            </h1>

            {/* Description - 改用白色提升对比度 */}
            <p className="text-base sm:text-lg md:text-xl text-white mb-7 sm:mb-10 leading-relaxed max-w-lg drop-shadow-md">
              {t('hero.description')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 mb-10">
              <button
                onClick={onExplore}
                className="bg-white text-slate-900 px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg hover:shadow-xl hover:bg-slate-50 active:bg-slate-200 focus:outline-none focus:ring-4 focus:ring-white/30 focus-visible:ring-4 transition-all text-center cursor-pointer"
              >
                {t('hero.exploreBtn')}
              </button>
              <button
                onClick={onLife}
                className="bg-white/10 text-white border border-white/30 backdrop-blur-md px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg hover:bg-white/25 hover:shadow-lg hover:border-white/40 active:bg-white/30 focus:outline-none focus:ring-4 focus:ring-white/30 focus-visible:ring-4 transition-all text-center cursor-pointer"
              >
                {t('hero.lifeBtn')}
              </button>
            </div>

            {/* Social Proof - 员工头像组 */}
            <div className="flex items-center gap-4">
              <div className="flex -space-x-3">
                {['/images/avatars/avatar-1.webp', '/images/avatars/avatar-2.webp', '/images/avatars/avatar-3.webp', '/images/avatars/avatar-4.webp'].map((avatar, index) => (
                  <img
                    key={index}
                    src={avatar}
                    alt={`Team member ${index + 1}`}
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-white/80 object-cover shadow-lg hover:scale-110 transition-transform"
                    loading="lazy"
                  />
                ))}
              </div>
              <div className="text-left">
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className="text-white font-semibold text-sm">4.8</span>
                </div>
                <p className="text-white/90 text-xs sm:text-sm">{t('hero.teamRating')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>


    </>
  );
};

export default Hero;
